"use client";

import { useEffect, useMemo, useState } from "react";
import Hemicycle from "./components/Hemicycle";

type SeatColor = "white" | "green" | "red" | "orange" | "black";
type ProvinceControl =
  | "Sécession"
  | "Autonomie"
  | "Sédition"
  | "Insoumission"
  | "Contestation"
  | "Équilibre"
  | "Stable"
  | "Prospère"
  | "Pacifié"
  | "Zone de Non-Droit"
  | "Contrôle Total"
  | "Insurrection"
  | "Défiance"
  | "Rayonnante";

type RegionalStateControl =
  | "En Guerre"
  | "Antagoniste"
  | "Rival"
  | "Prudent"
  | "Indifférent"
  | "Coopératif"
  | "Allié"
  | "Fantoche";

type ProvinceState = Record<string, ProvinceControl>;
type RegionalState = Record<string, RegionalStateControl>;

type PassedLaw = {
  title: string;
  text: string;
  abrogee?: boolean;
  adopteeSousEnclume?: boolean;
  organique?: boolean;
  nonConforme?: boolean;
  decret?: boolean;
};

const PROVINCE_CONTROL_OPTIONS: ProvinceControl[] = [
  "Autonomie",
  "Sécession",
  "Sédition",
  "Insoumission",
  "Contestation",
  "Équilibre",
  "Stable",
  "Prospère",
  "Pacifié",
  "Zone de Non-Droit",
  "Contrôle Total",
  "Insurrection",
  "Défiance",
  "Rayonnante",
];

const REGIONAL_STATE_OPTIONS: RegionalStateControl[] = [
  "En Guerre",
  "Antagoniste",
  "Rival",
  "Prudent",
  "Indifférent",
  "Coopératif",
  "Allié",
  "Fantoche",
];

const SUPER_MAJORITY_OPTIONS = ["3/5", "2/3", "7/10" ,"3/4"] as const;
const VETO_OPTIONS = ["none", "president", "player"] as const;
type VetoMode = (typeof VETO_OPTIONS)[number];

type EnclumeStatus = "idle" | "running" | "adopted" | "rejected";

type ConstitutionalStatus = "conforme" | "nonConforme" | "nonStatue";

type ChoiceModeOptionCount = 2 | 3;

export default function Home() {
  const [numSeats, setNumSeats] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [title, setTitle] = useState("");
  const [paragraph, setParagraph] = useState("");
  const [seatColors, setSeatColors] = useState<SeatColor[]>([]);
  const [presidentColor, setPresidentColor] = useState<SeatColor>("white");
  const [economyGauge, setEconomyGauge] = useState(5);
  const [socialGauge, setSocialGauge] = useState(5);
  const [securityGauge, setSecurityGauge] = useState(5);
  const [budgetGauge, setBudgetGauge] = useState(4);
  const [countrySituation, setCountrySituation] = useState("");
  const [isCrisis, setIsCrisis] = useState(false);
  const [crisisDescription, setCrisisDescription] = useState("");
  const [provinces, setProvinces] = useState<ProvinceState>({
    "La Capitale": "Prospère",
    "Le Plateau": "Équilibre",
    "Les Hauteurs Profondes": "Stable",
    "Vallée de l’Armoire": "Contestation",
    "Provinces des Plasticiens": "Contestation",
    "Archipel d'Eldrazi": "Équilibre",
  });
  const [regionalStates, setRegionalStates] = useState<RegionalState>({
    "L'Outre-Porte": "Prudent",
    "Alliance des Etats d'Elimat": "Indifférent",
    "Empire de Tori Value": "Rival",
    "Junte des Emirats du Sud": "Coopératif",
    "Royaume de Luvonie": "Indifférent",
  });
  const [passedLaws, setPassedLaws] = useState<PassedLaw[]>([]);
  const [lawFeedback, setLawFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [isControlValidated, setIsControlValidated] = useState<ConstitutionalStatus>("nonStatue");
  const [requiredMajority, setRequiredMajority] = useState<"simple" | "super">("simple");
  const [superMajorityRatio, setSuperMajorityRatio] = useState<string>("3/5");
  const [vetoMode, setVetoMode] = useState<VetoMode>("none");
  const [isNoConfidenceMotion, setIsNoConfidenceMotion] = useState(false);
  const [useEnclumeLaw, setUseEnclumeLaw] = useState(false);
  const [enclumeStatus, setEnclumeStatus] = useState<EnclumeStatus>("idle");
  const [enclumeStartedAt, setEnclumeStartedAt] = useState<number | null>(null);
  const [enclumeDurationMinutes, setEnclumeDurationMinutes] = useState(4);
  const [isSeatSelectionMode, setIsSeatSelectionMode] = useState(false);
  const [selectionRingColor, setSelectionRingColor] = useState("#3b82f6");
  const [selectedSeatOverlays, setSelectedSeatOverlays] = useState<Record<number, string>>({});
  const [selectedPresidentOverlay, setSelectedPresidentOverlay] = useState<string | null>(null);
  const [isEraseMode, setIsEraseMode] = useState(false);
  const [newProvinceName, setNewProvinceName] = useState("");
  const [isGoldOutlineMode, setIsGoldOutlineMode] = useState(false);
  const [goldOutlinedSeats, setGoldOutlinedSeats] = useState<number[]>([]);
  const [goldOutlinedPresident, setGoldOutlinedPresident] = useState(false);
  const [isSecretBallot, setIsSecretBallot] = useState(false);
  const [revealSecretResults, setRevealSecretResults] = useState(false);
  const [proposal1Title, setProposal1Title] = useState("");
  const [proposal1Text, setProposal1Text] = useState("");
  const [proposal2Title, setProposal2Title] = useState("");
  const [proposal2Text, setProposal2Text] = useState("");
  const [proposal3Title, setProposal3Title] = useState("");
  const [proposal3Text, setProposal3Text] = useState("");
  const [proposal1Organic, setProposal1Organic] = useState(false);
  const [proposal2Organic, setProposal2Organic] = useState(false);
  const [proposal3Organic, setProposal3Organic] = useState(false);
  const [proposal1Decret, setProposal1Decret] = useState(false);
  const [proposal2Decret, setProposal2Decret] = useState(false);
  const [proposal3Decret, setProposal3Decret] = useState(false);
  const [electionMode, setElectionMode] = useState(false);
  const [candidateCount, setCandidateCount] = useState(2);
  const [candidateNames, setCandidateNames] = useState<string[]>(["Candidat 1", "Candidat 2"]);
  const [candidateColors, setCandidateColors] = useState<string[]>(["#4f46e5", "#7c3aed"]);
  const [activeCandidateIndex, setActiveCandidateIndex] = useState(0);
  const [allowAntiConstitutionalAmendment, setAllowAntiConstitutionalAmendment] = useState(false);
  const [isDecretMode, setIsDecretMode] = useState(false);
  const [choiceMode, setChoiceMode] = useState(false);
  const [choiceOptionCount, setChoiceOptionCount] = useState<ChoiceModeOptionCount>(2);
  const [choiceUseProposals, setChoiceUseProposals] = useState(true);
  const [choiceCustomLabels, setChoiceCustomLabels] = useState<string[]>(["Choix 1", "Choix 2", "Choix 3"]);
  const [choiceColors, setChoiceColors] = useState<string[]>(["#22c55e", "#ef4444", "#f59e0b"]);
  const [activeChoiceIndex, setActiveChoiceIndex] = useState(0);

  const enclumeDurationMs = useMemo(
    () => enclumeDurationMinutes * 60 * 1000,
    [enclumeDurationMinutes]
  );

  useEffect(() => {
    setCandidateNames((prev) => {
      const next = prev.slice(0, candidateCount);
      while (next.length < candidateCount) next.push(`Candidat ${next.length + 1}`);
      return next;
    });
    setCandidateColors((prev) => {
      const palette = ["#4f46e5", "#7c3aed", "#2563eb", "#059669", "#dc2626", "#ea580c", "#0d9488", "#a21caf"];
      const next = prev.slice(0, candidateCount);
      while (next.length < candidateCount) next.push(palette[next.length % palette.length]);
      return next;
    });
    setActiveCandidateIndex((prev) => Math.min(prev, Math.max(0, candidateCount - 1)));
  }, [candidateCount]);

  useEffect(() => {
    setActiveChoiceIndex((prev) => Math.min(prev, Math.max(0, choiceOptionCount - 1)));
  }, [choiceOptionCount]);

  const setCandidateName = (idx: number, value: string) => {
    setCandidateNames((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const setCandidateColor = (idx: number, value: string) => {
    setCandidateColors((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const nextColor = (current: SeatColor, target: "seat" | "president"): SeatColor => {
    if (choiceMode) {
      const active = choiceColors[activeChoiceIndex] ?? choiceColors[0] ?? "#22c55e";
      const currentNorm = current.toLowerCase();
      return currentNorm === active.toLowerCase() ? "white" : (active as SeatColor);
    }

    if (electionMode) {
      const active = candidateColors[activeCandidateIndex] ?? candidateColors[0] ?? "#4f46e5";
      const currentNorm = current.toLowerCase();
      return currentNorm === active.toLowerCase() ? "white" : (active as SeatColor);
    }

    const seatCycleNoVeto: SeatColor[] = ["white", "green", "red", "black"];
    const seatCycleWithPlayerVeto: SeatColor[] = ["white", "green", "red", "orange", "black"];

    const presidentCycleNoVeto: SeatColor[] = ["white", "green", "red", "black"];
    const presidentCycleWithPlayerVeto: SeatColor[] = ["white", "green", "red", "orange", "black"];
    const presidentCycleWithPresidentVeto: SeatColor[] = ["white", "green", "orange", "black"];

    // Motion de censure: pas d'orange, uniquement blanc/vert/rouge/noir
    if (isNoConfidenceMotion) {
      const forcedCycle: SeatColor[] = ["white", "green", "red", "black"];
      return forcedCycle[(forcedCycle.indexOf(current) + 1) % forcedCycle.length];
    }

    const cycle =
      target === "seat"
        ? (vetoMode === "player" ? seatCycleWithPlayerVeto : seatCycleNoVeto)
        : (vetoMode === "player"
            ? presidentCycleWithPlayerVeto
            : vetoMode === "president"
              ? presidentCycleWithPresidentVeto
              : presidentCycleNoVeto);

    return cycle[(cycle.indexOf(current) + 1) % cycle.length];
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const num = parseInt(inputValue);
    if (num >= 5 && num <= 50) {
      setNumSeats(num);
      setSeatColors(Array(num).fill("white"));
      setPresidentColor("white");
    } else {
      alert("Entrez un nombre entre 5 et 50");
    }
  };

  const canEnableSelectionMode = useMemo(() => !isEraseMode, [isEraseMode]);
  const canEnableEraseMode = useMemo(() => !isSeatSelectionMode, [isSeatSelectionMode]);

  const toggleSelectionMode = () => {
    setIsSeatSelectionMode((prev) => {
      const next = !prev;
      if (next) setIsEraseMode(false);
      return next;
    });
  };

  const toggleEraseMode = () => {
    setIsEraseMode((prev) => {
      const next = !prev;
      if (next) setIsSeatSelectionMode(false);
      return next;
    });
  };

  const toggleSeat = (index: number) => {
    if (isGoldOutlineMode) {
      setGoldOutlinedSeats((prev) =>
        prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
      );
      return;
    }

    if (isSeatSelectionMode && !isEraseMode) {
      setSelectedSeatOverlays((prev) => {
        const next = { ...prev };
        if (next[index]) delete next[index];
        else next[index] = selectionRingColor;
        return next;
      });
      return;
    }

    if (isEraseMode && !isSeatSelectionMode) {
      setSeatColors((prev) => {
        const next = [...prev];
        next[index] = "white";
        return next;
      });
      return;
    }

    setSeatColors((prev) => {
      const next = [...prev];
      next[index] = nextColor(next[index], "seat");
      return next;
    });
  };

  const togglePresident = () => {
    if (isGoldOutlineMode) {
      setGoldOutlinedPresident((prev) => !prev);
      return;
    }

    if (isSeatSelectionMode && !isEraseMode) {
      setSelectedPresidentOverlay((prev) => (prev ? null : selectionRingColor));
      return;
    }

    if (isEraseMode && !isSeatSelectionMode) {
      setPresidentColor("white");
      return;
    }

    setPresidentColor((c) => nextColor(c, "president"));
  };

  // Reset uniquement les voix (couleurs de vote)
  const resetVotes = () => {
    if (numSeats === null) return;
    setSeatColors(Array(numSeats).fill("white"));
    setPresidentColor("white");
  };

  // Reset uniquement les cercles de sélection (annotations)
  const resetSelections = () => {
    setSelectedSeatOverlays({});
    setSelectedPresidentOverlay(null);
    setGoldOutlinedSeats([]);
    setGoldOutlinedPresident(false);
  };

  const canSaveLaw = useMemo(() => {
    if (
      isControlValidated === "conforme" ||
      isControlValidated === "nonStatue" ||
      allowAntiConstitutionalAmendment
    ) {
      return true;
    }
    return useEnclumeLaw && enclumeStatus === "adopted";
  }, [isControlValidated, useEnclumeLaw, enclumeStatus, allowAntiConstitutionalAmendment]);

  const addCurrentLawToPassed = () => {
    const lawTitle = title.trim();
    const lawText = paragraph.trim();

    if (!canSaveLaw) {
      setLawFeedback({
        type: "error",
        message:
          "Impossible d’adopter : loi non validée par la Cour constitutionnelle (sauf loi de l’Enclume adoptée).",
      });
      return;
    }

    if (!lawTitle && !lawText) {
      setLawFeedback({
        type: "error",
        message: "Impossible d’adopter : renseignez au moins un nom de loi ou un texte de loi.",
      });
      return;
    }

    const adoptedUnderEnclume = useEnclumeLaw && enclumeStatus === "adopted";
    const isOrganicLaw = requiredMajority === "super";
    const isNonConforme = isControlValidated === "nonConforme";
    const isDecret = isDecretMode;

    setPassedLaws((prev) => [
      {
        title: lawTitle || "Sans titre",
        text: lawText || "Sans texte",
        abrogee: false,
        adopteeSousEnclume: adoptedUnderEnclume,
        organique: isOrganicLaw,
        nonConforme: isNonConforme,
        decret: isDecret,
      },
      ...prev,
    ]);

    setLawFeedback({
      type: "success",
      message: `Loi ajoutée${lawTitle ? ` : ${lawTitle}` : ""}${
        isDecret
          ? " (Décret)."
          : adoptedUnderEnclume
          ? " (Adoptée sous loi de l’Enclume)."
          : isOrganicLaw
          ? " (Loi Organique)."
          : isNonConforme
          ? " (Loi non conforme)."
          : "."
      }`,
    });
  };

  const toggleLawAbrogation = (index: number) => {
    setPassedLaws((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      const current = next[index];
      const nextStatus = !current.abrogee;
      next[index] = { ...current, abrogee: nextStatus };
      setLawFeedback({
        type: "success",
        message: nextStatus
          ? `Loi abrogée${current.title ? ` : ${current.title}` : ""}.`
          : `Loi rétablie${current.title ? ` : ${current.title}` : ""}.`,
      });
      return next;
    });
  };

  const cycleSuperMajorityRatio = () => {
    setSuperMajorityRatio((prev) => {
      const idx = SUPER_MAJORITY_OPTIONS.indexOf(prev as (typeof SUPER_MAJORITY_OPTIONS)[number]);
      const nextIdx = idx === -1 ? 0 : (idx + 1) % SUPER_MAJORITY_OPTIONS.length;
      return SUPER_MAJORITY_OPTIONS[nextIdx];
    });
  };

  const cycleVetoMode = () => {
    setVetoMode((prev) => {
      const idx = VETO_OPTIONS.indexOf(prev);
      const nextIdx = idx === -1 ? 0 : (idx + 1) % VETO_OPTIONS.length;
      return VETO_OPTIONS[nextIdx];
    });
  };

  useEffect(() => {
    if (!lawFeedback) return;
    const t = setTimeout(() => setLawFeedback(null), 2500);
    return () => clearTimeout(t);
  }, [lawFeedback]);

  useEffect(() => {
    if (numSeats === null) return;
    const payload = {
      numSeats,
      title,
      paragraph,
      seatColors,
      presidentColor,
      economyGauge,
      socialGauge,
      securityGauge,
      budgetGauge,
      countrySituation,
      isCrisis,
      crisisDescription,
      provinces,
      regionalStates,
      passedLaws,
      isControlValidated,
      requiredMajority,
      superMajorityRatio,
      vetoMode,
      isNoConfidenceMotion,
      useEnclumeLaw,
      enclumeStatus,
      enclumeStartedAt,
      enclumeDurationMinutes,
      selectedSeatOverlays,
      selectedPresidentOverlay,
      goldOutlinedSeats,
      goldOutlinedPresident,
      isEraseMode,
      isSecretBallot,
      revealSecretResults,
      electionMode,
      candidateCount,
      candidateNames,
      candidateColors,
      activeCandidateIndex,
      isDecretMode,
      choiceMode,
      choiceOptionCount,
      choiceUseProposals,
      choiceCustomLabels,
      choiceColors,
      activeChoiceIndex,
      proposals: [
        { title: proposal1Title, text: proposal1Text, organique: proposal1Organic, decret: proposal1Decret },
        { title: proposal2Title, text: proposal2Text, organique: proposal2Organic, decret: proposal2Decret },
        { title: proposal3Title, text: proposal3Text, organique: proposal3Organic, decret: proposal3Decret },
      ],
    };

    localStorage.setItem("hemicycleState", JSON.stringify(payload));
    localStorage.setItem("hemicycleNotes", JSON.stringify(passedLaws));
  }, [
    numSeats,
    title,
    paragraph,
    seatColors,
    presidentColor,
    economyGauge,
    socialGauge,
    securityGauge,
    budgetGauge,
    countrySituation,
    isCrisis,
    crisisDescription,
    provinces,
    regionalStates,
    passedLaws,
    isControlValidated,
    requiredMajority,
    superMajorityRatio,
    vetoMode,
    isNoConfidenceMotion,
    useEnclumeLaw,
    enclumeStatus,
    enclumeStartedAt,
    enclumeDurationMinutes,
    selectedSeatOverlays,
    selectedPresidentOverlay,
    goldOutlinedSeats,
    goldOutlinedPresident,
    isEraseMode,
    isSecretBallot,
    revealSecretResults,
    electionMode,
    candidateCount,
    candidateNames,
    candidateColors,
    activeCandidateIndex,
    proposal1Title,
    proposal1Text,
    proposal2Title,
    proposal2Text,
    proposal3Title,
    proposal3Text,
    proposal1Organic,
    proposal2Organic,
    proposal3Organic,
    proposal1Decret,
    proposal2Decret,
    proposal3Decret,
    isDecretMode,
    choiceMode,
    choiceOptionCount,
    choiceUseProposals,
    choiceCustomLabels,
    choiceColors,
    activeChoiceIndex,
  ]);

  useEffect(() => {
    if (useEnclumeLaw) {
      if (!enclumeStartedAt || enclumeStatus === "idle") {
        setEnclumeStartedAt(Date.now());
        setEnclumeStatus("running");
      }
    } else {
      setEnclumeStartedAt(null);
      setEnclumeStatus("idle");
    }
  }, [useEnclumeLaw, enclumeStartedAt, enclumeStatus]);

  useEffect(() => {
    if (!useEnclumeLaw || enclumeStatus !== "running" || !enclumeStartedAt) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - enclumeStartedAt;
      if (elapsed >= enclumeDurationMs) {
        setEnclumeStatus("adopted");
      }
    }, 250);
    return () => clearInterval(interval);
  }, [useEnclumeLaw, enclumeStatus, enclumeStartedAt, enclumeDurationMs]);

  const canRejectEnclume = useMemo(
    () => useEnclumeLaw && enclumeStatus === "running",
    [useEnclumeLaw, enclumeStatus]
  );

  const canAdoptEnclume = useMemo(
    () => useEnclumeLaw && enclumeStatus === "running",
    [useEnclumeLaw, enclumeStatus]
  );

  // Si la motion de censure est activée, on supprime les oranges déjà posés
  useEffect(() => {
    if (!isNoConfidenceMotion) return;

    setSeatColors((prev) => prev.map((c) => (c === "orange" ? "red" : c)));
    setPresidentColor((prev) => (prev === "orange" ? "red" : prev));
  }, [isNoConfidenceMotion]);

  const addProvince = () => {
    const name = newProvinceName.trim();
    if (!name) return;
    setProvinces((prev) => {
      if (prev[name] || regionalStates[name]) return prev;
      return { ...prev, [name]: "Sécession" };
    });
    setNewProvinceName("");
  };

  const removeProvince = (name: string) => {
    setProvinces((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const renameProvince = (oldName: string, rawNewName: string) => {
    const newName = rawNewName.trim();
    if (!newName || newName === oldName) return;

    setProvinces((prev) => {
      if (!prev[oldName]) return prev;
      if (prev[newName] || regionalStates[newName]) return prev;

      const next: ProvinceState = {};
      (Object.keys(prev) as string[]).forEach((key) => {
        if (key === oldName) next[newName] = prev[oldName];
        else next[key] = prev[key];
      });
      return next;
    });
  };

  const setProvinceIndependence = (name: string) => {
    setProvinces((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setRegionalStates((prev) => ({ ...prev, [name]: "Indifférent" }));
  };

  const annexRegionalState = (name: string) => {
    setRegionalStates((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setProvinces((prev) => ({ ...prev, [name]: "Sécession" }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 dark:from-gray-900 dark:to-black p-8">
      {lawFeedback && (
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`px-4 py-3 rounded-lg shadow-lg text-sm font-semibold border ${
              lawFeedback.type === "success"
                ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950 dark:border-emerald-700 dark:text-emerald-200"
                : "bg-rose-50 border-rose-300 text-rose-800 dark:bg-rose-950 dark:border-rose-700 dark:text-rose-200"
            }`}
          >
            {lawFeedback.message}
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800 dark:text-white">
          Parlement de Gérionie
        </h1>

        {numSeats === null ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md mx-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre de sièges :
                </label>
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  min="5"
                  max="50"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Entre 5 et 50"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg transition"
              >
                Créer l&apos;hémicycle
              </button>
            </form>

            <button
              type="button"
              onClick={() => window.open("/depart", "_blank", "noopener,noreferrer")}
              className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg transition"
            >
              Voir la situation de départ
            </button>
            <button
              type="button"
              onClick={() => window.open("/carte", "_blank", "noopener,noreferrer")}
              className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg transition"
            >
              Ouvrir la carte
            </button>
          </div>
        ) : (
          <div>
            <div className="mb-4 space-y-4">
              {/* Loi + Texte de loi pleine largeur */}
              <div className="w-full bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <div className="space-y-4">
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Loi :
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Entrez le nom d'une Loi"
                    />
                  </div>
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Texte de loi :
                    </label>
                    <textarea
                      value={paragraph}
                      onChange={(e) => setParagraph(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Entrez le texte de loi"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 text-center">
                    Niveau de Contrôle des Provinces
                  </h3>

                  <div className="mb-3 grid grid-cols-[1fr_auto] gap-2">
                    <input
                      type="text"
                      value={newProvinceName}
                      onChange={(e) => setNewProvinceName(e.target.value)}
                      className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ajouter une province..."
                    />
                    <button
                      type="button"
                      onClick={addProvince}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition"
                    >
                      Ajouter
                    </button>
                  </div>

                  <div className="space-y-3">
                    {(Object.keys(provinces) as string[]).map((name) => (
                      <div key={name} className="grid grid-cols-[1fr_140px_auto_auto] gap-2 items-center">
                        <input
                          type="text"
                          defaultValue={name}
                          onBlur={(e) => renameProvince(name, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              renameProvince(name, (e.target as HTMLInputElement).value);
                              (e.target as HTMLInputElement).blur();
                            }
                          }}
                          className="min-w-0 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          title="Modifier le nom de la province"
                        />
                        <select
                          value={provinces[name]}
                          onChange={(e) =>
                            setProvinces((prev) => ({ ...prev, [name]: e.target.value as ProvinceControl }))
                          }
                          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {PROVINCE_CONTROL_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setProvinceIndependence(name)}
                          className="px-2 py-1.5 text-xs font-semibold rounded-md text-white bg-amber-600 hover:bg-amber-700 transition"
                        >
                          Indépendance
                        </button>
                        <button
                          type="button"
                          onClick={() => removeProvince(name)}
                          className="px-2 py-1.5 text-xs font-semibold rounded-md text-white bg-red-600 hover:bg-red-700 transition"
                        >
                          Supprimer
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 text-center">
                    Relations Internationales
                  </h3>
                  <div className="space-y-3">
                    {(Object.keys(regionalStates) as string[]).map((name) => (
                      <div key={name} className="grid grid-cols-[1fr_140px_auto] gap-2 items-center">
                        <span className="min-w-0 px-2 py-1.5 text-sm text-gray-900 dark:text-white">{name}</span>
                        <select
                          value={regionalStates[name]}
                          onChange={(e) =>
                            setRegionalStates((prev) => ({ ...prev, [name]: e.target.value as RegionalStateControl }))
                          }
                          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {REGIONAL_STATE_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => annexRegionalState(name)}
                          className="px-2 py-1.5 text-xs font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition"
                        >
                          Annexion
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 text-center">
                Pilotage du vote
              </h3>

              <div className="mb-3 text-center">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Avis de la Cour Constitutionnelle
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => setIsControlValidated("conforme")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      isControlValidated === "conforme" ? "bg-emerald-600" : "bg-emerald-500 hover:bg-emerald-600"
                    }`}
                  >
                    Loi conforme
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsControlValidated("nonConforme")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      isControlValidated === "nonConforme" ? "bg-rose-600" : "bg-rose-500 hover:bg-rose-600"
                    }`}
                  >
                    Loi non-conforme
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsControlValidated("nonStatue")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      isControlValidated === "nonStatue" ? "bg-gray-600" : "bg-gray-500 hover:bg-gray-600"
                    }`}
                  >
                    Cour n&apos;a pas statué
                  </button>
                </div>
              </div>

              <div className="text-center">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Type de loi et majorité requise
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => setRequiredMajority("simple")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      requiredMajority === "simple" ? "bg-blue-600" : "bg-blue-500 hover:bg-blue-600"
                    }`}
                  >
                    Majorité simple
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequiredMajority("super")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      requiredMajority === "super" ? "bg-violet-600" : "bg-violet-500 hover:bg-violet-600"
                    }`}
                  >
                    Super majorité
                  </button>
                  <button
                    type="button"
                    onClick={cycleSuperMajorityRatio}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md text-white transition bg-slate-600 hover:bg-slate-700"
                  >
                    Ratio supermajorité : {superMajorityRatio}
                  </button>
                  <button
                    type="button"
                    onClick={cycleVetoMode}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md text-white transition bg-amber-600 hover:bg-amber-700"
                  >
                    Véto : {vetoMode === "president" ? "Président" : vetoMode === "player" ? "Joueur" : "Aucun"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsNoConfidenceMotion((v) => !v)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      isNoConfidenceMotion ? "bg-red-700 hover:bg-red-800" : "bg-red-500 hover:bg-red-600"
                    }`}
                  >
                    Motion de censure : {isNoConfidenceMotion ? "Activée" : "Désactivée"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseEnclumeLaw((v) => !v)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      useEnclumeLaw ? "bg-emerald-700 hover:bg-emerald-800" : "bg-emerald-500 hover:bg-emerald-600"
                    }`}
                  >
                    Utiliser la loi de l&apos;Enclume : {useEnclumeLaw ? "Oui" : "Non"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEnclumeStatus("adopted")}
                    disabled={!canAdoptEnclume}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      canAdoptEnclume
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Adopter la loi de l&apos;Enclume
                  </button>
                  <button
                    type="button"
                    onClick={() => setEnclumeStatus("rejected")}
                    disabled={!canRejectEnclume}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      canRejectEnclume
                        ? "bg-rose-600 hover:bg-rose-700"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Rejeter la loi de l&apos;Enclume
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsDecretMode((v) => !v)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                      isDecretMode ? "bg-amber-700 hover:bg-amber-800" : "bg-amber-600 hover:bg-amber-700"
                    }`}
                  >
                    Décret : {isDecretMode ? "Activé" : "Désactivé"}
                  </button>
                </div>

                <div className="mt-4 max-w-sm mx-auto text-left">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Durée chrono Loi de l&apos;Enclume ({enclumeDurationMinutes} min)
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={7}
                    step={1}
                    value={enclumeDurationMinutes}
                    onChange={(e) => setEnclumeDurationMinutes(Number(e.target.value))}
                    className="w-full accent-amber-500"
                  />
                </div>
              </div>
            </div>

            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-red-200 dark:border-red-900">
                <label className="block text-sm font-semibold text-red-700 dark:text-red-300 mb-2">
                  Économie ({economyGauge}/10)
                </label>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={economyGauge}
                  onChange={(e) => setEconomyGauge(Number(e.target.value))}
                  className="w-full accent-red-500"
                />
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-900">
                <label className="block text-sm font-semibold text-green-700 dark:text-green-300 mb-2">
                  Social ({socialGauge}/10)
                </label>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={socialGauge}
                  onChange={(e) => setSocialGauge(Number(e.target.value))}
                  className="w-full accent-green-500"
                />
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-900">
                <label className="block text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
                  Sécurité ({securityGauge}/10)
                </label>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={securityGauge}
                  onChange={(e) => setSecurityGauge(Number(e.target.value))}
                  className="w-full accent-blue-500"
                />
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Budget ({budgetGauge > 0 ? `+${budgetGauge}` : budgetGauge})
                </label>
                <input
                  type="range"
                  min={-7}
                  max={7}
                  step={1}
                  value={budgetGauge}
                  onChange={(e) => setBudgetGauge(Number(e.target.value))}
                  className="w-full accent-slate-500"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                L&apos;essentiel de l&apos;info :
              </label>
              <textarea
                value={countrySituation}
                onChange={(e) => setCountrySituation(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Décrivez la situation actuelle..."
              />
            </div>

            <div className="mb-4">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={isCrisis}
                  onChange={(e) => setIsCrisis(e.target.checked)}
                  className="h-4 w-4"
                />
                Situation de crise
              </label>
            </div>

            {isCrisis && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description de la crise :
                </label>
                <textarea
                  value={crisisDescription}
                  onChange={(e) => setCrisisDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Décrivez la situation de crise..."
                />
              </div>
            )}

            {/* Nouveau bloc : au-dessus des propositions de loi */}
            <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
                Lois adoptées ({passedLaws.length})
              </h3>

              {passedLaws.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-300">Aucune loi adoptée pour le moment.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-auto pr-1">
                  {passedLaws.map((law, idx) => (
                    <div
                      key={`dashboard-law-${idx}-${law.title}`}
                      className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {law.title || "Sans titre"}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            {law.abrogee ? "Statut : Abrogée" : "Statut : En vigueur"}
                          </p>
                          {law.adopteeSousEnclume ? (
                            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                              Loi de l’Enclume
                            </p>
                          ) : null}
                          {law.organique ? (
                            <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                              Loi organique
                            </p>
                          ) : null}
                          {law.decret ? (
                            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                              Décret
                            </p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleLawAbrogation(idx)}
                          className={`px-2 py-1 text-xs font-semibold rounded-md text-white transition ${
                            law.abrogee
                              ? "bg-indigo-600 hover:bg-indigo-700"
                              : "bg-rose-600 hover:bg-rose-700"
                          }`}
                        >
                          {law.abrogee ? "Réinstituer" : "Abroger"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
                  Proposition de loi 1
                </h3>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={proposal1Title}
                    onChange={(e) => setProposal1Title(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Titre de la proposition"
                  />
                  <textarea
                    value={proposal1Text}
                    onChange={(e) => setProposal1Text(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Texte de la proposition"
                  />
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={proposal1Organic}
                        onChange={(e) => setProposal1Organic(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Loi organique
                    </label>
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={proposal1Decret}
                        onChange={(e) => setProposal1Decret(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Décret
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
                  Proposition de loi 2
                </h3>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={proposal2Title}
                    onChange={(e) => setProposal2Title(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Titre de la proposition"
                  />
                  <textarea
                    value={proposal2Text}
                    onChange={(e) => setProposal2Text(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Texte de la proposition"
                  />
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={proposal2Organic}
                        onChange={(e) => setProposal2Organic(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Loi organique
                    </label>
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={proposal2Decret}
                        onChange={(e) => setProposal2Decret(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Décret
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
                  Proposition de loi 3
                </h3>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={proposal3Title}
                    onChange={(e) => setProposal3Title(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Titre de la proposition"
                  />
                  <textarea
                    value={proposal3Text}
                    onChange={(e) => setProposal3Text(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Texte de la proposition"
                  />
                  <div className="flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={proposal3Organic}
                        onChange={(e) => setProposal3Organic(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Loi organique
                    </label>
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={proposal3Decret}
                        onChange={(e) => setProposal3Decret(e.target.checked)}
                        className="h-4 w-4"
                      />
                      Décret
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4 flex justify-end gap-3">
              <div className="flex flex-col gap-2">
                <button
                  onClick={addCurrentLawToPassed}
                  disabled={!canSaveLaw}
                  className={`px-4 py-2 text-white font-semibold rounded-lg transition ${
                    canSaveLaw
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                  title={
                    canSaveLaw
                      ? "Adopter la loi"
                      : "Loi non conforme à la Constitution (sauf Enclume adoptée ou amendement anti constitutionnel)"
                  }
                >
                  Adopter la loi
                </button>

                <label className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg inline-flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={allowAntiConstitutionalAmendment}
                    onChange={(e) => setAllowAntiConstitutionalAmendment(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Amendement anti constitutionnel
                </label>


                <label className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Selection Couleur Parti
                  </span>
                  <input
                    type="color"
                    value={selectionRingColor}
                    onChange={(e) => setSelectionRingColor(e.target.value)}
                    className="h-6 w-10 cursor-pointer"
                  />
                </label>


                <button
                  onClick={() => setIsGoldOutlineMode((v) => !v)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                    isGoldOutlineMode ? "bg-amber-700 hover:bg-amber-800" : "bg-amber-600 hover:bg-amber-700"
                  }`}
                >
                  Sélection Chef de Parti : {isGoldOutlineMode ? "ON" : "OFF"}
                </button>
              </div>

              <div className="flex flex-col gap-1.5">
                <button
                  onClick={toggleSelectionMode}
                  disabled={!canEnableSelectionMode && !isSeatSelectionMode}
                  className={`px-2.5 py-1.5 text-xs text-white font-semibold rounded-md transition ${
                    isSeatSelectionMode
                      ? "bg-blue-700 hover:bg-blue-800"
                      : !canEnableSelectionMode
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isSeatSelectionMode ? "Mode sélection: ON" : "Mode sélection: OFF"}
                </button>

                <button
                  onClick={toggleEraseMode}
                  disabled={!canEnableEraseMode && !isEraseMode}
                  className={`px-2.5 py-1.5 text-xs text-white font-semibold rounded-md transition ${
                    isEraseMode
                      ? "bg-rose-700 hover:bg-rose-800"
                      : !canEnableEraseMode
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-rose-600 hover:bg-rose-700"
                  }`}
                >
                  {isEraseMode ? "Mode effacement: ON" : "Mode effacement: OFF"}
                </button>

                <button
                  onClick={resetVotes}
                 className="px-2.5 py-1.5 text-xs bg-violet-700 hover:bg-violet-800 text-white font-semibold rounded-md transition"
                >
                  Reset des voix
                </button>

                <button
                  onClick={resetSelections}
                  className="px-2.5 py-1.5 text-xs bg-slate-600 hover:bg-slate-700 text-white font-semibold rounded-md transition"
                >
                  Reset des partis politiques
                </button>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={() => window.open("/notes", "_blank", "noopener,noreferrer")}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition"
                >
                  Ouvrir l&apos;historique des lois votées
                </button>
                <button
                  onClick={() => window.open("/display", "_blank", "noopener,noreferrer")}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition"
                >
                  Ouvrir l’affichage plein écran
                </button>
                <button
                  onClick={() => window.open("/propositions", "_blank", "noopener,noreferrer")}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition"
                >
                  Ouvrir les 3 propositions
                </button>
                <button
                  onClick={() => window.open("/depart", "_blank", "noopener,noreferrer")}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition"
                >
                  Ouvrir la situation de départ
                </button>
                <button
                  onClick={() => window.open("/carte", "_blank", "noopener,noreferrer")}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition"
                >
                  Ouvrir la carte
                </button>
                <button
                  onClick={() =>
                    setIsSecretBallot((v) => {
                      const next = !v;
                      if (!next) setRevealSecretResults(false);
                      return next;
                    })
                  }
                  className={`px-4 py-2 text-white font-semibold rounded-lg transition ${
                    isSecretBallot ? "bg-gray-700 hover:bg-gray-800" : "bg-gray-600 hover:bg-gray-700"
                  }`}
                >
                  {isSecretBallot ? "Désactiver le bulletin secret" : "Activer le bulletin secret"}
                </button>

                {isSecretBallot && (
                  <button
                    onClick={() => setRevealSecretResults((v) => !v)}
                    className={`px-4 py-2 text-white font-semibold rounded-lg transition ${
                      revealSecretResults ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                  >
                    {revealSecretResults ? "Masquer les résultats" : "Afficher les résultats"}
                  </button>
                )}
              </div>
            </div>

            <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 text-center">
                Mode élection
              </h3>
              <div className="flex flex-wrap gap-3 items-center justify-center">
                <button
                  type="button"
                  onClick={() => setElectionMode((v) => !v)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                    electionMode ? "bg-indigo-700 hover:bg-indigo-800" : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  {electionMode ? "Désactiver l'élection" : "Activer l'élection"}
                </button>

                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Nombre de candidats ({candidateCount})
                </label>
                <input
                  type="range"
                  min={1}
                  max={8}
                  step={1}
                  value={candidateCount}
                  onChange={(e) => setCandidateCount(Number(e.target.value))}
                  className="w-40 accent-indigo-500"
                />
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                {candidateNames.slice(0, candidateCount).map((name, idx) => (
                  <div key={`cand-wrap-${idx}`} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setCandidateName(idx, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={`Candidat ${idx + 1}`}
                    />
                    <input
                      type="color"
                      value={candidateColors[idx] ?? "#4f46e5"}
                      onChange={(e) => setCandidateColor(idx, e.target.value)}
                      className="h-9 w-11 cursor-pointer rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      title={`Couleur candidat ${idx + 1}`}
                    />
                  </div>
                ))}
              </div>

              {electionMode && (
                <div className="mt-3 max-w-sm mx-auto">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 text-center">
                    Candidat actif pour le clic
                  </label>
                  <select
                    value={activeCandidateIndex}
                    onChange={(e) => setActiveCandidateIndex(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {candidateNames.slice(0, candidateCount).map((name, idx) => (
                      <option key={`active-cand-${idx}`} value={idx}>
                        {name?.trim() || `Candidat ${idx + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 text-center">
                Mode choix (2 ou 3 options)
              </h3>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setChoiceMode((v) => !v)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                    choiceMode ? "bg-indigo-700 hover:bg-indigo-800" : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  {choiceMode ? "Désactiver le mode choix" : "Activer le mode choix"}
                </button>

                <button
                  type="button"
                  onClick={() => setChoiceOptionCount(2)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                    choiceOptionCount === 2 ? "bg-slate-700 hover:bg-slate-800" : "bg-slate-600 hover:bg-slate-700"
                  }`}
                >
                  2 options
                </button>

                <button
                  type="button"
                  onClick={() => setChoiceOptionCount(3)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md text-white transition ${
                    choiceOptionCount === 3 ? "bg-slate-700 hover:bg-slate-800" : "bg-slate-600 hover:bg-slate-700"
                  }`}
                >
                  3 options
                </button>

                <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 ml-2">
                  <input
                    type="checkbox"
                    checked={choiceUseProposals}
                    onChange={(e) => setChoiceUseProposals(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Utiliser les propositions de loi
                </label>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                {Array.from({ length: choiceOptionCount }).map((_, idx) => (
                  <label
                    key={`choice-color-${idx}`}
                    className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-between gap-2"
                  >
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                      Couleur Choix {idx + 1}
                    </span>
                    <input
                      type="color"
                      value={choiceColors[idx] ?? "#22c55e"}
                      onChange={(e) =>
                        setChoiceColors((prev) => {
                          const next = [...prev];
                          next[idx] = e.target.value;
                          return next;
                        })
                      }
                      className="h-6 w-10 cursor-pointer"
                    />
                  </label>
                ))}
              </div>

              {!choiceUseProposals && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                  {Array.from({ length: choiceOptionCount }).map((_, idx) => (
                    <input
                      key={`choice-custom-${idx}`}
                      type="text"
                      value={choiceCustomLabels[idx] ?? ""}
                      onChange={(e) =>
                        setChoiceCustomLabels((prev) => {
                          const next = [...prev];
                          next[idx] = e.target.value;
                          return next;
                        })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={`Texte du choix ${idx + 1}`}
                    />
                  ))}
                </div>
              )}

              {choiceMode && (
                <div className="mt-3 max-w-sm mx-auto">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1 text-center">
                    Choix actif pour le clic
                  </label>
                  <select
                    value={activeChoiceIndex}
                    onChange={(e) => setActiveChoiceIndex(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {Array.from({ length: choiceOptionCount }).map((_, idx) => (
                      <option key={`active-choice-${idx}`} value={idx}>
                        {(choiceUseProposals
                          ? proposalChoicesLabel(idx, proposal1Title, proposal2Title, proposal3Title)
                          : (choiceCustomLabels[idx] || `Choix ${idx + 1}`)).trim() || `Choix ${idx + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <Hemicycle
              numSeats={numSeats}
              title={title}
              paragraph={paragraph}
              seatColors={seatColors}
              presidentColor={presidentColor}
              onToggleSeat={toggleSeat}
              onTogglePresident={togglePresident}
              economyGauge={economyGauge}
              socialGauge={socialGauge}
              securityGauge={securityGauge}
              budgetGauge={budgetGauge}
              countrySituation={countrySituation}
              isCrisis={isCrisis}
              crisisDescription={crisisDescription}
              provinces={provinces}
              regionalStates={regionalStates}
              isControlValidated={isControlValidated}
              requiredMajority={requiredMajority}
              superMajorityRatio={superMajorityRatio}
              vetoMode={vetoMode}
              isNoConfidenceMotion={isNoConfidenceMotion}
              useEnclumeLaw={useEnclumeLaw}
              enclumeStatus={enclumeStatus}
              enclumeStartedAt={enclumeStartedAt}
              enclumeDurationMinutes={enclumeDurationMinutes}
              selectedSeatOverlays={selectedSeatOverlays}
              selectedPresidentOverlay={selectedPresidentOverlay}
              goldOutlinedSeats={goldOutlinedSeats}
              goldOutlinedPresident={goldOutlinedPresident}
              isSecretBallot={isSecretBallot}
              hideAssemblyWhenSecretBallot={false}
              revealSecretResults={revealSecretResults}
              electionMode={electionMode}
              candidateNames={candidateNames.slice(0, candidateCount)}
              candidateColors={candidateColors.slice(0, candidateCount)}
              isDecretMode={isDecretMode}
              choiceMode={choiceMode}
              choiceOptionCount={choiceOptionCount}
              choiceUseProposals={choiceUseProposals}
              choiceCustomLabels={choiceCustomLabels}
              choiceColors={choiceColors}
              proposalChoices={[
                { title: proposal1Title, text: proposal1Text, organique: proposal1Organic, decret: proposal1Decret },
                { title: proposal2Title, text: proposal2Text, organique: proposal2Organic, decret: proposal2Decret },
                { title: proposal3Title, text: proposal3Text, organique: proposal3Organic, decret: proposal3Decret },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const proposalChoicesLabel = (idx: number, p1: string, p2: string, p3: string) => {
  const arr = [p1, p2, p3];
  return arr[idx] || `Choix ${idx + 1}`;
};
